import React, { useState } from "react";
import { Link } from "react-router";
import { CheckCircle2, ChevronRight } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "../../app/components/ui/alert";
import { Button } from "../../app/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../app/components/ui/card";
import { Progress } from "../../app/components/ui/progress";
import { ProfileStepFields, type ProfileStepId } from "./components/ProfileStepFields";
import { useBusinessProfile } from "./hooks/useBusinessProfile";

const STEPS = [
  { id: "BASIC_INFO", title: "Basic info", description: "Store type and display details" },
  { id: "LOCATION", title: "Location", description: "Address" },
  { id: "CONTACT", title: "Contact", description: "Phone and support" },
  { id: "COMPLIANCE", title: "Compliance", description: "Licenses and tax IDs" },
  { id: "POS_CONFIGURATION", title: "POS setup", description: "Defaults for checkout" },
] as const;

export function BusinessOnboardingPage() {
  const { profile, error, loading, saving, saveStep } = useBusinessProfile();

  const activeStepId = (profile?.next_step ?? "BASIC_INFO") as ProfileStepId;
  const storeType = profile?.store_type ?? null;

  const [form, setForm] = useState<Record<string, string>>({});

  React.useEffect(() => {
    if (!profile?.profile_data) return;
    const key = activeStepId as keyof typeof profile.profile_data;
    const existing = profile.profile_data[key];
    if (existing && typeof existing === "object") {
      const next: Record<string, string> = {};
      for (const [k, v] of Object.entries(existing)) {
        if (k === "tax_inclusive_pricing" || k === "low_stock_alert_enabled") {
          next[k] = v === true || v === "true" || v === 1 ? "true" : "false";
        } else {
          next[k] = v === null || v === undefined ? "" : String(v);
        }
      }
      setForm(next);
    } else {
      setForm({});
    }
  }, [profile, activeStepId]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    const payload: Record<string, unknown> = { ...form };
    if (activeStepId === "POS_CONFIGURATION") {
      payload.tax_inclusive_pricing =
        form.tax_inclusive_pricing === "true" || form.tax_inclusive_pricing === "on";
    }
    await saveStep(activeStepId, payload);
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30">
        <p className="text-muted-foreground">Loading onboarding…</p>
      </div>
    );
  }

  if (profile?.is_complete) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30 p-6">
        <Card className="max-w-md w-full">
          <CardHeader>
            <div className="flex items-center gap-2 text-emerald-600">
              <CheckCircle2 className="h-6 w-6" />
              <CardTitle>Profile complete</CardTitle>
            </div>
            <CardDescription>Your business profile is ready. You can use the full POS.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Button asChild className="w-full">
              <Link to="/">Go to dashboard</Link>
            </Button>
            <Button asChild variant="outline" className="w-full">
              <Link to="/settings/business-profile">View business profile</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const progress = profile?.completion_percentage ?? 0;

  return (
    <div className="min-h-screen bg-muted/30 py-10 px-4">
      <div className="max-w-3xl mx-auto space-y-8">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Business profile</h1>
          <p className="text-muted-foreground mt-1">
            Complete each step to unlock POS and inventory. You can revisit completed steps anytime.
          </p>
        </div>

        <div className="space-y-2">
          <div className="flex justify-between text-sm text-muted-foreground">
            <span>Progress</span>
            <span>{progress}%</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        <ol className="flex flex-wrap gap-2">
          {STEPS.map((s, i) => {
            const done = profile?.completed_steps?.includes(s.id);
            const current = s.id === activeStepId;
            return (
              <li
                key={s.id}
                className={[
                  "flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-medium",
                  done ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "",
                  current && !done ? "border-primary bg-primary/5 text-primary" : "",
                  !done && !current ? "border-border text-muted-foreground" : "",
                ].join(" ")}
              >
                <span className="opacity-70">{i + 1}.</span>
                {s.title}
                {done ? <CheckCircle2 className="h-3.5 w-3.5 ml-1" /> : null}
              </li>
            );
          })}
        </ol>

        {error ? (
          <Alert variant="destructive">
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : null}

        <Card>
          <CardHeader>
            <CardTitle>{STEPS.find((s) => s.id === activeStepId)?.title ?? "Step"}</CardTitle>
            <CardDescription>
              {STEPS.find((s) => s.id === activeStepId)?.description ?? ""}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={onSubmit} className="space-y-4">
              <ProfileStepFields
                activeStepId={activeStepId}
                form={form}
                setForm={setForm}
                storeType={storeType}
              />

              <div className="flex justify-end pt-2">
                <Button type="submit" disabled={saving}>
                  {saving ? "Saving…" : "Save & continue"}
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        <p className="text-center text-sm text-muted-foreground">
          <Link to="/" className="text-primary hover:underline">
            Back to dashboard
          </Link>
        </p>
      </div>
    </div>
  );
}
